"use client";

import type { LeadFlowDocument, LeadFlowStepErrors } from "../types";

import { useCallback, useDeferredValue, useEffect, useState } from "react";
import { useLocale } from "~www/components/LocaleProvider";
import { useLeadFlowAnswerStore } from "../components/LeadFlowAnswerStoreProvider";
import { NO_ERRORS, NO_STEPS } from "../constants/empty-values";
import { getStepErrors } from "../utils/get-step-errors";
import { getStepParameterName } from "../utils/get-step-parameter-name";
import { resolveStepIndex } from "../utils/resolve-step-index";
import { useAnswers, useAnswersLoaded } from "./use-answers";
import {
  countPushedSteps,
  goBackInHistory,
  useStepParameter,
} from "./use-step-parameter";

type UseLeadFlowNavigationInput = {
  blockKey: string;
  leadFlow: LeadFlowDocument;
  /**
   * The URL step parameter when the submit succeeded. The thank-you screen
   * shows while the URL stays there, and the deep-link guard holds meanwhile
   * since submit clears the answers. Compared with both the live and the
   * deferred URL: the live one so a step change still settling toward it
   * can't hide it, the deferred one so it holds until a step change away
   * from it settles.
   */
  thankYouStepParameter: string | null | undefined;
};

type ExitStatus = "idle" | "settling" | "walking";

type StepErrorsState = {
  errors: LeadFlowStepErrors;
  stepId: string;
};

function hasErrors(errors: LeadFlowStepErrors): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * One lead flow's position, kept in its URL step parameter, and the
 * validation errors shown on the current step. Until stored answers load a
 * deep link can't be checked, so the first step shows, as on the server.
 */
function useNavigation({
  blockKey,
  leadFlow,
  thankYouStepParameter,
}: UseLeadFlowNavigationInput) {
  const steps = leadFlow.steps ?? NO_STEPS;
  const store = useLeadFlowAnswerStore();
  const answers = useAnswers();
  const hasLoadedAnswers = useAnswersLoaded();
  const { context } = useLocale();
  const parameterName = getStepParameterName(blockKey);
  const [urlStepParameter, setStepParameter] = useStepParameter(parameterName);
  // The URL is an external store, whose updates always render synchronously.
  // Deferring it shows each step change in a background render, which is what
  // lets `<ViewTransition>` animate it for Continue, Back, and browser
  // history alike.
  const stepParameter = useDeferredValue(urlStepParameter);
  // The deferred step is what the transition is still showing. Another
  // Continue in that window would leave the same step again.
  const isStepChangePending = stepParameter !== urlStepParameter;
  const [stepErrors, setStepErrors] = useState<StepErrorsState | null>(null);
  // Until the walk back to the first step lands and the deferred step catches
  // up, the step shown is still the one being left, which would keep a
  // closed surface open.
  const [exitStatus, setExitStatus] = useState<ExitStatus>("idle");
  if (exitStatus === "settling" && !isStepChangePending) {
    setExitStatus("idle");
  }
  const isExiting = exitStatus !== "idle";

  const { shouldResetParameter, stepIndex } = resolveStepIndex({
    answers,
    leadFlow,
    stepParameter,
  });
  // Matching either URL keeps the thank-you up until a step change away from
  // it has settled, so the transition never shows the step it replaced.
  const isShowingThankYou =
    thankYouStepParameter === urlStepParameter ||
    thankYouStepParameter === stepParameter;
  const currentStepIndex = hasLoadedAnswers && !isExiting ? stepIndex : 0;
  const currentStep = steps[currentStepIndex];
  const errors =
    currentStep && stepErrors?.stepId === currentStep.id
      ? stepErrors.errors
      : NO_ERRORS;

  useEffect(() => {
    if (hasLoadedAnswers && shouldResetParameter && !isShowingThankYou) {
      setStepParameter(null, { replace: true });
    }
  }, [
    hasLoadedAnswers,
    isShowingThankYou,
    setStepParameter,
    shouldResetParameter,
  ]);

  // The first step drops the parameter, so it shares the URL a visitor
  // entered the page on.
  const goToStep = useCallback(
    (index: number, options?: { replace?: boolean }) => {
      const step = steps[index];
      if (step) {
        setStepParameter(index === 0 ? null : step.id, options);
      }
    },
    [setStepParameter, steps],
  );

  const clearErrors = useCallback(() => {
    setStepErrors(null);
  }, []);

  const showErrors = useCallback(
    (stepId: string, nextErrors: LeadFlowStepErrors) => {
      setStepErrors({ errors: nextErrors, stepId });
    },
    [],
  );

  /** Validates the current step and returns whether it can be left. */
  const validateCurrentStep = useCallback((): boolean => {
    if (!currentStep) {
      return false;
    }

    // Read the store directly: an auto-advance fires after the render that
    // recorded the pick.
    const nextErrors = getStepErrors({
      answers: store.getSnapshot(),
      deliveryContext: leadFlow.deliveryContext,
      localeCode: context.code,
      step: currentStep,
    });
    if (hasErrors(nextErrors)) {
      showErrors(currentStep.id, nextErrors);
      return false;
    }

    setStepErrors(null);
    return true;
  }, [context.code, currentStep, leadFlow.deliveryContext, showErrors, store]);

  /** Moves to the next step when the current one is valid, and reports whether it moved. */
  const advance = useCallback((): boolean => {
    if (
      !hasLoadedAnswers ||
      isExiting ||
      isStepChangePending ||
      currentStepIndex >= steps.length - 1 ||
      !validateCurrentStep()
    ) {
      return false;
    }

    goToStep(currentStepIndex + 1);
    return true;
  }, [
    currentStepIndex,
    goToStep,
    hasLoadedAnswers,
    isExiting,
    isStepChangePending,
    steps.length,
    validateCurrentStep,
  ]);

  const goBack = useCallback(() => {
    if (currentStepIndex <= 0 || isStepChangePending) {
      return;
    }

    // Continue pushed this entry, so Back walks history. Pushing the
    // previous step would make browser Back return to the step just left.
    if (countPushedSteps(parameterName) > 0) {
      goBackInHistory();
      return;
    }

    goToStep(currentStepIndex - 1, { replace: true });
  }, [currentStepIndex, goToStep, isStepChangePending, parameterName]);

  /**
   * Returns to the first step, walking back over every entry this flow's
   * Continue pushed. Replacing only the current entry would leave the earlier
   * steps' entries behind it, and browser Back would reopen the flow there.
   */
  const exitToFirstStep = useCallback(() => {
    const pushedStepCount = countPushedSteps(parameterName);
    const returnToFirstStep = () => goToStep(0, { replace: true });

    if (pushedStepCount === 0) {
      returnToFirstStep();
      return;
    }

    const hasStartedWalk = goBackInHistory(pushedStepCount, () => {
      returnToFirstStep();
      setExitStatus("settling");
    });

    if (!hasStartedWalk) {
      returnToFirstStep();
      return;
    }

    setExitStatus("walking");
  }, [goToStep, parameterName]);

  return {
    advance,
    clearErrors,
    currentStep,
    currentStepIndex,
    errors,
    exitToFirstStep,
    goBack,
    goToStep,
    hasLoadedAnswers,
    isShowingThankYou,
    liveStepParameter: urlStepParameter,
    showErrors,
    stepParameter,
    validateCurrentStep,
  };
}

export { useNavigation };
